import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges } from "@angular/core";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { EqValidator } from "../../interfaces";
import { EqFormService } from "../eqform.service";
import { Observable, of } from "rxjs";
import { startWith, debounceTime, distinctUntilChanged, map, switchMap } from "rxjs/operators";
import { MatRippleModule } from "@angular/material/core";
import { CommonModule } from "@angular/common";
import { RoleLabelPipe } from "../../pipe/role-label.pipe";

export interface Options {
  id: number;
  name: string;
  'member Iea #'?: string;
  city?: string;
  state?: string;
  'team Id'?: string;
  'team name'?: string;
  discipline?: string;
  levels?: string;
  [key: string]: any;
  role?: string;
  status?: string;
  subStatus?: string;
  email?: string;
  mobile?: string;
}

@Component({
  selector: 'eq-textInputSearch',
  templateUrl: 'text-input-search.component.html',
  styleUrls: ['text-input-search.component.scss'],
  standalone: true,
  imports: [
    MatInputModule,
    MatFormFieldModule,
    MatRippleModule,
    MatIconModule,
    MatAutocompleteModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RoleLabelPipe
  ]
})
export class TextInputSearchComponent implements OnInit, OnDestroy {
  @Input() apiUrl: any; // API URL to fetch options
  @Input() label: string = 'Sample';
  @Input() apiQuery: Array<{name: string, value: any}> = [];
  @Input() header: string = '';
  @Input() appearance: any = 'outline';
  @Input() name: string = '';
  @Input() icon: string = '';
  @Input() form: any;
  @Input() validators: EqValidator[] = [];
  @Output() onChange = new EventEmitter<any>();
  @Output() onSelect = new EventEmitter<any>();
  @Input() dataPresent: boolean = false;
  @Input() searchFor: string = 'users';
  @Input() returnObject: boolean = false;
  @Input() searchType: string = 'users';
  @Input() isDisable: boolean = false;
  @Output('onBlur') onBlur = new EventEmitter<string>();

  controlName: FormControl = new FormControl();
  filteredOptions!: Observable<Options[]>;

  mergeAccountsKeyMappings: any = {
    id: 'ID', 
    name: 'Name', 
    email: 'Email', 
    mobile: 'Mobile',
    guardianName: "Guardian Name",
    membershipId: "Membership ID",
    role: "Role",
    'member Iea #': 'IEA #'
  }

  showAwardsKeyMappings: any = {
    id: 'ID', 
    name: 'Name', 
    email: 'Email', 
    mobile: 'Mobile',
    teamId: "Team Id",
    teamName: "Team Name",
    membershipId: "Membership ID",
    role: "Role",
    'member Iea #': 'IEA #'
  }

  constructor(private formService: EqFormService) {}

  ngOnInit(): void {
    if (!this.form || !this.name) {
      const errMsg: string = !this.form
        ? 'form attribute is missing.'
        : 'name attribute is missing.';
      throw Error(errMsg);
    }
    this.formService.addFormControl(this.form, this.name, this.validators);
    if(this.isDisable) this.form.get(this.name)?.disable();
    this.controlName = this.form.controls[this.name];
    this.controlName.valueChanges.subscribe(value => {
      this.search(value);
    });
    this.fillOption();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes['searchFor']?.currentValue) {
      this.searchType = this.searchFor;
    }
    if(changes['isDisable']) {
      this.isDisable = changes['isDisable']?.currentValue;
      if(this.isDisable) {
        this.form.get(this.name)?.disable();
      }else{
        this.form.get(this.name)?.enable();
      }
    }
  }

  onBlurEvent(e: any) {
    this.onBlur.emit(e.target.value);
  }

  fillOption() {
    this.filteredOptions = this.controlName.valueChanges.pipe(
      startWith(''),
      debounceTime(400),
      distinctUntilChanged(),
      switchMap((value: any) => {
        if (value) {
          let query;
          if (this.dataPresent) {
            query = {
              name: value?.name
            };
          } else {
            query = {
              name: value
            };
          }
          return this.formService.search(query, this.apiUrl, this.apiQuery).pipe(
            map((resp: any) => {
              if (resp?.status === 200 || resp?.data?.length) {
                this.dataPresent = false;
                return resp?.data;
              } else {
                return [];
              }
            })
          );
        } else {
          return of([]);
        }    
      })
    );
  }

  search(event: any) {
    if (event !== '') {
      this.onChange.emit(event);
    }
  }

  selectedOption(option: Options) {
    if(this.returnObject) {
      this.onSelect.emit(option);
    } else {
      this.onSelect.emit(option.id);
    }
  }

  trackByFn(index: number, option: Options): any {
    return option.id;
  }
  
  displayFn(option: Options): string {
    if(option && option.name && option.name !== '') {
      return option.name;
    } else if(option && option['team name'] && option['team name'] !== '') {
      return option['team name'];
    } else if(option && option["firstName"] && option["lastName"] !== '') {
      const fullName = option["firstName"] + ' ' + option["lastName"]
      return fullName;
    } else {
      return '';
    }
  }

  getErrorMessage() {
    return this.formService.getErrorMessage(
      this.form,
      this.name,
      this.validators
    );
  }

  hasError() {
    return this.formService.hasError(this.form, this.name);
  }

  isValid(){
    return this.formService.isValid(this.form, this.name);
  }

  ngOnDestroy(): void {
    this.form.removeControl(this.name);
  }
}
